import { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircle,
  XCircle,
  Search,
  CalendarDays,
  HelpCircle,
} from "lucide-react";
import DateRangePicker from "../../../Component/Common/DateRangePicker";
import { BASE_URL } from "../../../config/api";
import SearchInput from "../../../Component/Common/SearchInput";
import { formatDateTime } from "../../../Component/Common/formatDateTime";

export default function ActivationReport() {
  const [view, setView] = useState("activity");
  const [weeks, setWeeks] = useState([]);
  const [search, setSearch] = useState("");
  const [hoverCard, setHoverCard] = useState(null);

  const [selectedState, setSelectedState] = useState("");
  const [districtList, setDistrictList] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [pincodeList, setPincodeList] = useState([]);
  const [selectedPincode, setSelectedPincode] = useState("");
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // DEFAULT CURRENT MONTH DATES
  const today = new Date();

  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [fromDate, setFromDate] = useState(firstDay);
  const [toDate, setToDate] = useState(lastDay);

  const [rows, setRows] = useState([]);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);


  const stateList = [
  { code: "AP", state: "ANDHRA PRADESH" },
  { code: "TG", state: "TELANGANA" },
  { code: "AS", state: "ASSAM" },
  { code: "BR", state: "BIHAR" },
  { code: "CT", state: "CHHATTISGARH" },
  { code: "DL", state: "DELHI" },
  { code: "GA", state: "GOA" },
  { code: "GJ", state: "GUJARAT" },
  { code: "HR", state: "HARYANA" },
  { code: "HP", state: "HIMACHAL PRADESH" },
  { code: "JK", state: "JAMMU AND KASHMIR" },
  { code: "JH", state: "JHARKHAND" },
  { code: "KA", state: "KARNATAKA" },
  { code: "KL", state: "KERALA" },
  { code: "MP", state: "MADHYA PRADESH" },
  { code: "MH", state: "MAHARASHTRA" },
  { code: "OD", state: "ODISHA" },
  { code: "PB", state: "PUNJAB" },
  { code: "RJ", state: "RAJASTHAN" },
  { code: "TN", state: "TAMIL NADU" },
  { code: "UP", state: "UTTAR PRADESH" },
  { code: "UK", state: "UTTARAKHAND" },
  { code: "WB", state: "WEST BENGAL" },
];

  // GENERATE DATE RANGE
  const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const generateDates = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const days = [];

  while (startDate <= endDate) {
    days.push(formatDate(startDate));
    startDate.setDate(startDate.getDate() + 1);
  }

  return days;
};

  // GENERATE WEEK GROUPING
  const generateWeeks = (dates) => {
  const weeks = [];
  let week = [];

  dates.forEach((date, index) => {
    const d = new Date(date);

    // ignore previous month dates
    if (d.getMonth() !== new Date(fromDate).getMonth()) return;

    week.push(date);

    if (week.length === 7 || index === dates.length - 1) {
      weeks.push({
        label: `${new Date(week[0]).getDate()}-${new Date(
          week[week.length - 1]
        ).getDate()}`,
        title: `Week ${weeks.length + 1}`,
        days: [...week],
      });

      week = [];
    }
  });

  return weeks;
};

  useEffect(() => {
    setWeeks(generateWeeks(dates));
  }, [dates]);

  const fetchDistricts = async (stateName) => {
  try {
    setLoadingDistricts(true);
    setDistrictList([]);

    const res = await axios.get(
      `https://goelectronix.in/api/v1/org_user/statedistrict?state=${stateName}`
    );

    setDistrictList(res.data || []);
  } catch (error) {
    console.error("District fetch error", error);
  } finally {
    setLoadingDistricts(false);
  }
};

const searchRetailer = async (value) => {
  if (!value) {
    fetchReport();
    return;
  }

  try {
    const res = await axios.get(
      "https://uatwarranty.goelectronix.in/api/common/get-users",
      {
        params: {
          role: 5,
          search_value: value,
          page: 1,
          company_id: 3,
        },
      }
    );

    const retailers = res.data?.data || [];

    const retailerCodes = retailers.map((r) => r.company_code);

    const filtered = rows.filter((r) =>
      retailerCodes.includes(r.retailer_code)
    );

    setRows(filtered);
  } catch (err) {
    console.error("Search error", err);
  }
};


useEffect(() => {
  const delay = setTimeout(() => {
    if (search) searchRetailer(search);
  }, 500);

  return () => clearTimeout(delay);
}, [search]);


  // FETCH API
  const fetchReport = async () => {
  try {
    setLoading(true);

    const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    const end = new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 0);
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    const params = {
      company_id: 3,
      from_date: formatDate(start),
      to_date: formatDate(end),
    };

    if (selectedState) {
      params.state = selectedState;
    }

    if (selectedDistrict) {
      params.district = selectedDistrict;
    }

    if (selectedPincode) {
      params.pincode = selectedPincode;
    }

    const res = await axios.get(
      `${BASE_URL}company/retailer-warranty-sales-report`,
      { params }
    );

    setRows(res.data.data);
    setDates(generateDates(start, end));
  } catch (error) {
    console.error(error);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchReport();
    }, [
      fromDate,
      toDate,
      selectedState,
      selectedDistrict,
      selectedPincode,
    ]);

  const grandTotals = dates.map((date) => {
  let count = 0;
  let amount = 0;

  rows.forEach((r) => {
    const activity = r.activity?.[date];

    count += activity?.sale_count || 0;
    amount += activity?.sale_amount || 0;
  });

  return {
    count,
    amount,
  };
});

const grandActivationTotal = grandTotals.reduce(
  (sum, d) => sum + d.count,
  0
);

const grandAmountTotal = grandTotals.reduce(
  (sum, d) => sum + d.amount,
  0
);

const monthBase = fromDate;
const monthLabel = monthBase.toLocaleString("default", {
  month: "long",
  year: "numeric",
});

const todayStr = new Date().toISOString().split("T")[0];
const todayDate = new Date().toISOString().split("T")[0];


const resetFilters = () => {
  setSelectedState("");
  setSelectedDistrict("");
  setSelectedPincode("");
  setDistrictList([]);
  setSearch("");

  // Reset date to current month
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  setFromDate(firstDay);
  setToDate(lastDay);

  fetchReport();
};


const TableSkeleton = () => {
  return (
    <tbody>
      {[...Array(8)].map((_, row) => (
        <tr key={row} className="animate-pulse border-b border-slate-100">
          
          {/* Retailer column */}
          <td className="p-3 sticky left-0 bg-white">
            <div className="space-y-2">
              <div className="h-3 w-32 bg-slate-200 rounded"></div>
              <div className="h-3 w-20 bg-slate-200 rounded"></div>
            </div>
          </td>

          {/* Date columns */}
          {[...Array(dates.length || 31)].map((_, col) => (
            <td key={col} className="p-3">
              <div className="h-4 w-4 mx-auto bg-slate-200 rounded-full"></div>
            </td>
          ))}

          {/* Total column */}
          <td className="p-3">
            <div className="h-4 w-8 mx-auto bg-slate-200 rounded"></div>
          </td>

        </tr>
      ))}
    </tbody>
  );
};


  return (
    <div className="p-1 bg-[#F8FAFC] min-h-screen font-sans">

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-3 px-2 py-3 flex items-center justify-between flex-wrap gap-2">

  {/* LEFT SIDE */}
  <div className="flex items-center gap-2 flex-wrap">

    {/* Tabs */}
    <div className="flex bg-slate-100 p-1 rounded-lg">
      {[
        { key: "activity", label: "Activity" },
        { key: "activation", label: "Activation" },
        { key: "amount", label: "Amount" },
      ].map((tab) => (
        <button
          key={tab.key}
          onClick={() => setView(tab.key)}
          className={`px-1.5 py-1.5 rounded-md text-sm font-semibold transition-all ${
            view === tab.key
              ? "bg-white text-blue-600 shadow"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>

    {/* Divider */}
    <div className="w-px h-7 bg-slate-200" />

    {/* State */}
    <select
      value={selectedState}
      onChange={(e) => {
        const state = e.target.value;
        setSelectedState(state);
        fetchDistricts(state);
      }}
      className="h-9 border border-slate-200 rounded-lg px-3 text-sm bg-slate-50 focus:ring-2 focus:ring-blue-100 outline-none"
    >
      <option value="">State</option>
      {stateList.map((s) => (
        <option key={s.code} value={s.state}>
          {s.state}
        </option>
      ))}
    </select>

    {/* District */}
    <select
      value={selectedDistrict}
      onChange={(e) => setSelectedDistrict(e.target.value)}
      className="h-9 border border-slate-200 rounded-lg px-3 text-sm bg-slate-50 focus:ring-2 focus:ring-blue-100 outline-none"
    >
      <option value="">District</option>
      {districtList.map((d) => (
        <option key={d.id} value={d.district}>
          {d.district}
        </option>
      ))}
    </select>

    {/* Pincode */}
    <input
      type="text"
      value={selectedPincode}
      onChange={(e) => setSelectedPincode(e.target.value)}
      placeholder="Pincode"
      className="h-9 w-[200px] border border-slate-200 rounded-lg px-3 text-sm bg-slate-50 focus:ring-2 focus:ring-blue-100 outline-none"
    />

    {/* Search */}
    <SearchInput
      value={search}
      onChange={setSearch}
      onClear={() => setSearch("")}
      placeholder="Search retailer"
      className="h-9 min-w-[100px] max-w-[300px]"
    />
  </div>

  {/* RIGHT SIDE */}
  <div className="flex items-center gap-3">

    {/* <div className="flex items-center text-xs font-medium text-slate-500 gap-1">
      <CalendarDays size={14} className="text-blue-500" />
      {monthLabel}
    </div> */}

    <DateRangePicker
      startDate={fromDate}
      endDate={toDate}
      onChange={(start) => {
        if (!start) return;

        const monthStart = new Date(start);
        monthStart.setDate(1);

        const monthEnd = new Date(start);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(0);

        setFromDate(monthStart);
        setToDate(monthEnd);
      }}
    />
    <button
      onClick={resetFilters}
      className="h-9 px-3 text-sm font-medium rounded-lg border border-slate-200 
      bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition"
    >
      Reset
    </button>
  </div>

  
</div>

      {/* TABLE */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-200/40 overflow-visible">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="min-w-full text-sm text-center border-separate border-spacing-0">
            <thead>
              {/* WEEK HEADER */}
              <tr className="bg-white">
                <th className="sticky left-0 bg-white z-30 border-b border-r border-slate-100"></th>

                {weeks.map((w, idx) => (
                  <th
                    key={idx}
                    colSpan={w.days.length}
                    className="px-4 border-b border-r border-slate-100"
                  >
                    <div className="flex justify-between items-center px-2">
                      <span className="text-xs uppercase font-bold text-slate-400 tracking-widest">
                        {w.label}
                      </span>
                      <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                        {w.title}
                      </span>
                    </div>
                  </th>
                ))}

                <th className="border-b border-slate-100"></th>
              </tr>

              {/* DATE HEADER */}
              <tr className="bg-slate-50/80">
                <th className="p-4 text-left sticky left-0 bg-[#F1F5F9] z-20 w-[240px] border-b border-r border-slate-200 font-bold text-slate-700">
                  Retailer Details
                </th>

                {dates.map((d) => {
                  const day = new Date(d).getDate();
                  const weekday = new Date(d).toLocaleDateString("en-US", {
                    weekday: "short",
                  });

                  return (
                    <th
                      key={d}
                      className={`p-2 text-xs font-bold border-b border-r border-slate-100
                      ${d === todayStr ? "bg-blue-600 text-white" : "text-slate-500"}`}
                    >
                      <div className="flex flex-col items-center min-w-[32px]">
                        <span className="opacity-70 text-xs">
                          {weekday}
                        </span>
                        <span className="text-sm">{day}</span>
                      </div>
                    </th>
                  );
                })}

                <th className="p-2 text-xs font-bold bg-blue-600 text-white border-b sticky right-0 z-20">
                  Total
                </th>
              </tr>
            </thead>

            {loading ? (
              <TableSkeleton />
            ) : (
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => {
                const values = dates.map((date) => {
                  const activity = r.activity?.[date];

                  return {
                    sale: activity?.is_sale || false,
                    count: activity?.sale_count || 0,
                    amount: activity?.sale_amount || 0,
                  };
                });

                const activationTotal = values.reduce(
                  (sum, v) => sum + v.count,
                  0
                );

                const amountTotal = values.reduce(
                  (sum, v) => sum + v.amount,
                  0
                );

                const onboardDate = r.onboard_date?.split("T")[0];
                const isNewRetailer = onboardDate === todayDate;

                return (

                  
                  <tr
                    key={i}
                    className={`transition-colors group
                    ${isNewRetailer
                      ? "bg-emerald-100 border-l-4 border-green-500"
                      : "hover:bg-blue-50/40"}`}
                  >
                   <td
                      className={`p-2 text-left sticky left-0 z-[5] border-r border-slate-200 min-w-[200px] text-xs
                      ${isNewRetailer ? "bg-emerald-100" : "bg-white hover:bg-[#F8FAFC]"}`}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();

                        setHoverCard({
                          retailer: r,
                          x: rect.right + 10,
                          y: rect.top,
                        });
                      }}
                      onMouseLeave={() => setHoverCard(null)}
                    >
                      {isNewRetailer && (
                        <span className="text-[9px] bg-green-400 text-white px-1.5 py-0.5 rounded">
                          NEW
                        </span>
                      )}

                      <div className="font-bold text-slate-900">{r.retailer_code}</div>
                      <div className="text-slate-700 text-xs">{r.retailer_name}</div>
                    </td>

                    {values.map((v, idx) => (
                     <td
                        key={idx}
                        className={`p-2 border-r border-r-slate-50
                        ${dates[idx] === todayStr ? "bg-blue-50 ring-1 ring-blue-200" : ""}`}
                      >
                        <div className="flex justify-center items-center">
                          {view === "activity" && (() => {
                            const cellDate = new Date(dates[idx]);
                            const today = new Date();
                            const onboard = r.onboard_date ? new Date(r.onboard_date) : null;

                            // Future date → ?
                            if (cellDate > today) {
                              return <HelpCircle size={16} strokeWidth={3} className="text-yellow-500 " />;
                            }

                            // Before retailer onboard → ?
                            if (onboard && cellDate < onboard) {
                              return <HelpCircle size={16} strokeWidth={3} className="text-slate-500" />;
                            }

                            // Sale happened
                            if (v.sale) {
                              return <CheckCircle size={16} strokeWidth={3} className="text-emerald-500" />;
                            }

                            // After onboard but no sale
                            return <XCircle size={16} strokeWidth={3} className="text-rose-400" />;
                          })()}

                          {view === "activation" && (
                            <span
                              className={`font-bold ${
                                v.count
                                  ? "text-slate-900"
                                  : "text-slate-500"
                              }`}
                            >
                              {v.count}
                            </span>
                          )}

                          {view === "amount" && (
                            <span
                              className={`text-sm font-extrabold ${
                                v.amount
                                  ? "text-blue-600"
                                  : "text-slate-500"
                              }`}
                            >
                              {v.amount ? `₹${v.amount}` : "0"}
                            </span>
                          )}
                        </div>
                      </td>
                    ))}

                    <td className="p-2 font-bold bg-blue-50 text-blue-700 sticky right-0 z-10 border-l border-blue-100">
                      {view === "amount"
                        ? `₹${amountTotal}`
                        : activationTotal}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            )}
            <tfoot>
              <tr className="bg-slate-900 text-white font-bold border-t">
                <td className="p-4 text-right sticky left-0 bg-slate-900 z-10 border-r border-slate-800 rounded-bl-2xl">
                  Grand Total
                </td>

                {grandTotals.map((g, idx) => (
                  <td
                    key={idx}
                    className={`p-2 border-r border-slate-800 ${
                      (idx + 1) % 7 === 0 ? "bg-slate-800" : ""
                    }`}
                  >
                    <span className="text-xs text-green-400">
                      {view === "amount" ? `₹${g.amount}` : g.count}
                    </span>
                  </td>
                ))}

                <td className="p-2 bg-blue-600 text-white rounded-br-2xl sticky right-0 z-10">
                  {view === "amount"
                    ? `₹${grandAmountTotal}`
                    : grandActivationTotal}
                </td>
              </tr>
            </tfoot>
          </table>

          {hoverCard && (
            <div
              style={{
                position: "fixed",
                top: hoverCard.y,
                left: hoverCard.x,
                zIndex: 99999,
              }}
              className="w-[260px] bg-white border border-slate-200 shadow-xl rounded-lg p-3 text-xs"
            >
              <div className="font-semibold text-slate-800 mb-1">
                {hoverCard.retailer.retailer_name}
              </div>

              <div className="text-slate-500 mb-2">
                Code: {hoverCard.retailer.retailer_code}
              </div>

              <div className="space-y-1 text-slate-600">
                <div className="flex justify-between">
                  <span>State</span>
                  <span>{hoverCard.retailer.state}</span>
                </div>

                <div className="flex justify-between">
                  <span>District</span>
                  <span>{hoverCard.retailer.district}</span>
                </div>

                <div className="flex justify-between">
                  <span>Pincode</span>
                  <span>{hoverCard.retailer.pincode}</span>
                </div>

                <div className="flex justify-between">
                  <span>Onboard</span>
                  <span>{formatDateTime(hoverCard.retailer.onboard_date)}</span>
                </div>
              </div>
            </div>
          )}
                  </div>
      </div>
    </div>
  );
}

// import { useState } from "react";
// import {
//   CheckCircle,
//   XCircle,
//   Search,
//   CalendarDays,
// } from "lucide-react";
// import DateRangePicker from "../../../Component/Common/DateRangePicker";

// export default function ActivationReport() {
//   const [view, setView] = useState("activity");

//   // Dates for the month
//   const dates = Array.from({ length: 30 }, (_, i) => i + 1);

//   // Define weeks based on dates for the header grouping
//   const weeks = [
//     { label: "Jun 1-7", title: "Week 1", days: dates.slice(0, 7) },
//     { label: "Jun 8-14", title: "Week 2", days: dates.slice(7, 14) },
//     { label: "Jun 15-21", title: "Week 3", days: dates.slice(14, 21) },
//     { label: "Jun 22-30", title: "Week 4+", days: dates.slice(21) },
//   ];

//   const rows = Array.from({ length: 10 }, (_, i) => ({
//     id: i + 1,
//     code: "RTL" + (1200 + i),
//     name: "Retailer " + (i + 1),
//     onboard: "12 Jan 2025",
//     state: "Maharashtra",
//     district: "Mumbai",
//     pincode: "400001",
//   }));

//   return (
//     <div className="p-2 bg-[#F8FAFC] min-h-screen font-sans">
//       {/* HEADER */}
//       <div className=" flex items-center justify-between">
//         {/* <div>
//           <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
//             Activation Report
//           </h1>
//           <p className="text-sm text-slate-500 mt-1">
//             Track retailer activity, activation and daily performance
//           </p>
//         </div> */}

        
//       </div>

//       {/* FILTERS */}
//       <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-3 grid grid-cols-6 gap-3">
//         <select className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none appearance-none bg-slate-50">
//           <option>Select State</option>
//         </select>
//         <select className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none appearance-none bg-slate-50">
//           <option>Select District</option>
//         </select>
//         <select className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none appearance-none bg-slate-50">
//           <option>Select Pincode</option>
//         </select>
//         <div className="flex items-center border border-slate-200 rounded-lg px-3 bg-slate-50 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
//           <Search size={15} className="text-slate-400" />
//           <input
//             type="text"
//             placeholder="Search retailer"
//             className="flex-1 p-2 outline-none text-sm bg-transparent"
//           />
//         </div>
//         {/* <input type="date" className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 outline-none" />
//         <input type="date" className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 outline-none" /> */}
//         <DateRangePicker
//           startDate={fromDate}
//           endDate={toDate}
//           onChange={(start, end) => {
//             setFromDate(start);
//             setToDate(end);
//           }}
//         />
//       </div>

//       {/* VIEW TABS */}
//       <div className="flex justify-between ">
//         <div className="flex bg-slate-200/50 p-1 rounded-xl w-fit gap-1 mb-3">
//         {[
//           { key: "activity", label: "Activity" },
//           { key: "activation", label: "Activation" },
//           { key: "amount", label: "Amount" },
//         ].map((tab) => (
//           <button
//             key={tab.key}
//             onClick={() => setView(tab.key)}
//             className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
//               view === tab.key
//                 ? "bg-white text-blue-600 shadow-sm"
//                 : "text-slate-500 hover:text-slate-700"
//             }`}
//           >
//             {tab.label}
//           </button>
//         ))}

        
//       </div>

//      <div>
//          <div className="bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm text-xs font-medium text-slate-600 flex items-center gap-2">
//           <CalendarDays size={14} className="text-blue-500" />
//           Monthly View: June 2026
//         </div>
//         </div>
//         </div>

//       {/* TABLE */}
//       <div className="bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-200/40 overflow-hidden">
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-sm text-center border-separate border-spacing-0">
//             <thead>
//               {/* TOP LEVEL WEEK GROUPING */}
//               <tr className="bg-white">
//                 <th className="sticky left-0 bg-white z-30 border-b border-r border-slate-100"></th>
//                 {weeks.map((w, idx) => (
//                   <th key={idx} colSpan={w.days.length} className="py-2 px-4 border-b border-r border-slate-100 last:border-r-0">
//                     <div className="flex justify-between items-center px-2">
//                       <span className="text-xs uppercase font-bold text-slate-400 tracking-widest">{w.label}</span>
//                       <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{w.title}</span>
//                     </div>
//                   </th>
//                 ))}
//                 <th className="border-b border-slate-100"></th>
//               </tr>

//               <tr className="bg-slate-50/80">
//                 <th className="p-4 text-left sticky left-0 bg-[#F1F5F9] z-20 w-[240px] border-b border-r border-slate-200 font-bold text-slate-700">
//                   Retailer Details
//                 </th>

//                 {dates.map((d) => (
//                   <th
//                     key={d}
//                     className={`p-2 text-xs font-bold text-slate-500 border-b ${
//                       d % 7 === 0 ? "border-r border-slate-200" : "border-r border-slate-100"
//                     } ${d === 12 ? "bg-blue-600 text-white" : ""}`}
//                   >
//                     <div className="flex flex-col items-center min-w-[35px]">
//                       <span className="opacity-70 text-[9px]">{["M", "T", "W", "T", "F", "S", "S"][(d - 1) % 7]}</span>
//                       <span className="text-sm">{d}</span>
//                     </div>
//                   </th>
//                 ))}

//                 <th className="p-2 text-xs font-bold bg-blue-600 text-white border-b sticky right-0 z-20">
//                   Total
//                 </th>
//               </tr>
//             </thead>

//             <tbody className="divide-y divide-slate-100">
//               {rows.map((r, i) => {
//                 const values = dates.map(() => (Math.random() > 0.6 ? 1 : 0));
//                 const activationTotal = values.reduce((sum, v) => sum + v, 0);
//                 const amountTotal = activationTotal * 199;

//                 return (
//                   <tr key={i} className="hover:bg-blue-50/40 transition-colors group">
//                     <td className="p-4 text-left sticky left-0 bg-white group-hover:bg-[#F8FAFC] z-10 border-r border-slate-200">
//                       <div className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{r.code}</div>
//                       <div className="text-slate-500 text-xs font-medium">{r.name}</div>
//                       <div className="text-xs text-slate-400 mt-1 flex gap-1 items-center">
//                         <span className="bg-slate-100 px-1 rounded">On: {r.onboard}</span>
//                       </div>
//                     </td>

//                     {values.map((v, idx) => {
//                       const day = idx + 1;
//                       return (
//                         <td
//                           key={idx}
//                           className={`p-2 border-r transition-all ${
//                             day % 7 === 0 ? "border-r-slate-200 bg-slate-50/30" : "border-r-slate-50"
//                           } ${day === 12 ? "bg-blue-50/50 ring-1 ring-inset ring-blue-100" : ""}`}
//                         >
//                           <div className="flex justify-center items-center">
//                             {view === "activity" &&
//                               (v ? (
//                                 <CheckCircle size={16} className="text-emerald-500 drop-shadow-sm" />
//                               ) : (
//                                 <XCircle size={16} className="text-slate-200" />
//                               ))}

//                             {view === "activation" && (
//                               <span className={`font-bold ${v > 0 ? "text-slate-900" : "text-slate-200"}`}>
//                                 {v ? "8" : "0"}
//                               </span>
//                             )}

//                             {view === "amount" && (
//                               <span className={`text-xs font-bold ${v > 0 ? "text-blue-600" : "text-slate-200"}`}>
//                                 {v ? `₹${v * 199}` : "0"}
//                               </span>
//                             )}
//                           </div>
//                         </td>
//                       );
//                     })}

//                     <td className="p-2 font-bold bg-blue-50 text-blue-700 sticky right-0 z-10 border-l border-blue-100">
//                       {view === "amount" ? `₹${amountTotal}` : activationTotal}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>

//             <tfoot>
//               <tr className="bg-slate-900 text-white font-bold border-t">
//                 <td className="p-4 text-right sticky left-0 bg-slate-900 z-10 border-r border-slate-800 rounded-bl-2xl">
//                   Grand Total
//                 </td>

//                 {dates.map((d) => {
//                   const random = Math.floor(Math.random() * 10);
//                   return (
//                     <td key={d} className={`p-2 border-r border-slate-800 ${d % 7 === 0 ? "bg-slate-800" : ""}`}>
//                       <span className="text-xs text-green-500">
//                         {view === "amount" ? `₹${random * 199}` : random}
//                       </span>
//                     </td>
//                   );
//                 })}

//                 <td className="p-2 bg-blue-600 text-white rounded-br-2xl sticky right-0 z-10">
//                   {view === "amount" ? "₹9,999" : "50"}
//                 </td>
//               </tr>
//             </tfoot>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// }

// import { useState } from "react";
// import { CheckCircle, XCircle, Search } from "lucide-react";

// export default function ActivationReport() {
//   const [view, setView] = useState("activity");

//   const dates = Array.from({ length: 31 }, (_, i) => i + 1);

//   const rows = Array.from({ length: 10 }, (_, i) => ({
//     id: i + 1,
//     code: "RTL" + (1200 + i),
//     name: "Retailer " + (i + 1),
//     onboard: "12 Jan 2025",
//     state: "Maharashtra",
//     district: "Mumbai",
//     pincode: "400001",
//   }));

//   return (
//     <div className="p-6 bg-slate-50 min-h-screen">

//       {/* Page Title */}
//       <div className="mb-4">
//         <h1 className="text-xl font-semibold text-slate-800">
//           Activation Report
//         </h1>
//         <p className="text-sm text-slate-500">
//           Track retailer activity, activation and daily performance
//         </p>
//       </div>

//       {/* Filters */}
//       <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm mb-5 grid grid-cols-6 gap-3">

//         <select className="border border-slate-200 rounded-xl px-3 py-2 text-sm">
//           <option>Select State</option>
//         </select>

//         <select className="border border-slate-200 rounded-xl px-3 py-2 text-sm">
//           <option>Select District</option>
//         </select>

//         <select className="border border-slate-200 rounded-xl px-3 py-2 text-sm">
//           <option>Select Pincode</option>
//         </select>

//         <div className="flex items-center border border-slate-200 rounded-xl px-3">
//           <Search size={16} className="text-slate-400" />
//           <input
//             type="text"
//             placeholder="Search retailer"
//             className="flex-1 p-2 outline-none text-sm"
//           />
//         </div>

//         <input
//           type="date"
//           className="border border-slate-200 rounded-xl px-3 py-2 text-sm"
//         />

//         <input
//           type="date"
//           className="border border-slate-200 rounded-xl px-3 py-2 text-sm"
//         />

//       </div>

//       {/* Tabs */}
//       <div className="flex gap-3 mb-5">
//         {[
//           { key: "activity", label: "Activity" },
//           { key: "activation", label: "Activation" },
//           { key: "amount", label: "Amount" },
//         ].map((tab) => (
//           <button
//             key={tab.key}
//             onClick={() => setView(tab.key)}
//             className={`px-5 py-2 rounded-xl text-sm font-semibold transition ${
//               view === tab.key
//                 ? "bg-blue-600 text-white shadow"
//                 : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
//             }`}
//           >
//             {tab.label}
//           </button>
//         ))}
//       </div>

//       {/* Table */}
//       <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">

//         <table className="min-w-full text-sm text-center">

//           {/* Header */}
//           <thead className="bg-slate-100 sticky top-0 z-10">
//             <tr>
//                 <th className="p-3 text-left">Sr</th>
//                 <th className="p-3 text-left w-[260px] min-w-[260px] max-w-[260px]">
//                     Retailer
//                     </th>

//                 {dates.map((d) => (
//                 <th key={d} className="p-2 text-xs font-semibold">
//                     {d}
//                 </th>
//                 ))}

//                 <th className="p-2 text-xs font-semibold bg-slate-200">
//                 Total
//                 </th>
//             </tr>
//             </thead>

//           {/* Body */}
//           <tbody>
//             {rows.map((r, i) => {

//                 const values = dates.map(() => (Math.random() > 0.6 ? 1 : 0));
//                 const activationTotal = values.reduce((sum, v) => sum + v, 0);
//                 const amountTotal = activationTotal * 199;

//                 return (
//                 <tr key={i} className="border-t hover:bg-slate-50 transition">

//                     <td className="p-3 font-medium">{r.id}</td>

//                     <td className="p-3 text-left w-[260px] min-w-[260px] max-w-[260px]">
//                     <div className="font-semibold text-slate-800">{r.code}</div>
//                     <div className="text-slate-600">{r.name}</div>
//                     <div className="text-xs text-slate-400">Onboard: {r.onboard}</div>
//                     <div className="text-xs text-slate-500">
//                         {r.state} • {r.district} • {r.pincode}
//                     </div>
//                     </td>

//                     {values.map((v, idx) => (
//                         <td key={idx} className="p-2">
//                             {view === "activity" && (
//                             v ? (
//                                 <CheckCircle size={16} className="text-green-600 mx-auto" />
//                             ) : (
//                                 <XCircle size={16} className="text-red-500 mx-auto" />
//                             )
//                             )}

//                             {view === "activation" && (
//                             <span className="font-medium">{v}</span>
//                             )}

//                             {view === "amount" && (
//                             <span className="font-medium text-blue-600">
//                                 ₹{v * 199}
//                             </span>
//                             )}
//                         </td>
//                         ))}

//                     {/* Row Total */}
//                     <td className="p-2 font-semibold text-blue-600">

//                     {view === "activity" && activationTotal}

//                     {view === "activation" && activationTotal}

//                     {view === "amount" && `₹${amountTotal}`}

//                     </td>

//                 </tr>
//                 );
//             })}
//             </tbody>

//           {/* Footer */}
//           <tfoot>
//             <tr className="bg-slate-50 font-semibold border-t">
//                 <td colSpan={2} className="p-3 text-right">
//                 Grand Total
//                 </td>

//                 {dates.map((d) => {
//                 const random = Math.floor(Math.random() * 10);

//                 return (
//                     <td key={d} className="p-2">

//                     {view === "activity" && random}

//                     {view === "activation" && random}

//                     {view === "amount" && `₹${random * 199}`}

//                     </td>
//                 );
//                 })}

//                 <td className="p-2 bg-slate-200">
//                 {view === "amount" ? "₹9999" : "50"}
//                 </td>

//             </tr>
//             </tfoot>

//         </table>

//       </div>
//     </div>
//   );
// }


// import { useState } from "react";

// export default function ActivationReport() {
//   const [view, setView] = useState("activity");

//   const dates = Array.from({ length: 31 }, (_, i) => i + 1);

//   const rows = Array.from({ length: 10 }, (_, i) => ({
//     id: i + 1,
//     code: "RTL" + (1200 + i),
//     name: "Retailer " + (i + 1),
//     onboard: "12 Jan 2025",
//     state: "Maharashtra",
//     district: "Mumbai",
//     pincode: "400001",
//   }));

//   return (
//     <div className="p-6 bg-gray-50 min-h-screen">
//       {/* Filters */}
//       <div className="bg-white p-4 rounded-2xl shadow mb-4 grid grid-cols-6 gap-4">
//         <select className="border rounded-xl p-2">
//           <option>Select State</option>
//         </select>

//         <select className="border rounded-xl p-2">
//           <option>Select District</option>
//         </select>

//         <select className="border rounded-xl p-2">
//           <option>Select Pincode</option>
//         </select>

//         <input
//           type="text"
//           placeholder="Search retailer"
//           className="border rounded-xl p-2"
//         />

//         <input type="date" className="border rounded-xl p-2" />

//         <input type="date" className="border rounded-xl p-2" />
//       </div>

//       {/* Buttons */}
//       <div className="flex gap-4 mb-4">
//         <button
//           onClick={() => setView("activity")}
//           className={`px-5 py-2 rounded-xl ${
//             view === "activity" ? "bg-blue-600 text-white" : "bg-white"
//           }`}
//         >
//           Activity
//         </button>

//         <button
//           onClick={() => setView("activation")}
//           className={`px-5 py-2 rounded-xl ${
//             view === "activation" ? "bg-blue-600 text-white" : "bg-white"
//           }`}
//         >
//           Activation
//         </button>

//         <button
//           onClick={() => setView("amount")}
//           className={`px-5 py-2 rounded-xl ${
//             view === "amount" ? "bg-blue-600 text-white" : "bg-white"
//           }`}
//         >
//           Amount
//         </button>
//       </div>

//       {/* Table */}
//       <div className="overflow-x-auto bg-white rounded-2xl shadow">
//         <table className="min-w-full text-sm text-center">
//           <thead className="bg-gray-100">
//             <tr>
//               <th className="p-2">Sr</th>
//               <th className="p-2">Retailer</th>
//               <th className="p-2">Location</th>

//               {dates.map((d) => (
//                 <th key={d} className="p-2 w-10">
//                   {d}
//                 </th>
//               ))}
//             </tr>
//           </thead>

//           <tbody>
//             {rows.map((r, i) => (
//               <tr key={i} className="border-t">
//                 <td className="p-2">{r.id}</td>

//                 <td className="p-2 text-left">
//                   <div className="font-semibold">{r.code}</div>
//                   <div>{r.name}</div>
//                   <div className="text-xs text-gray-500">
//                     Onboard: {r.onboard}
//                   </div>
//                 </td>

//                 <td className="p-2 text-left">
//                   <div>{r.state}</div>
//                   <div>{r.district}</div>
//                   <div>{r.pincode}</div>
//                 </td>

//                 {dates.map((d) => (
//                   <td key={d} className="p-2">
//                     {Math.random() > 0.6 ? (
//                       <span className="text-green-600">✓</span>
//                     ) : (
//                       <span className="text-red-500">✕</span>
//                     )}
//                   </td>
//                 ))}
//               </tr>
//             ))}
//           </tbody>

//           {/* Footer totals */}
//           <tfoot>
//             <tr className="bg-gray-50 font-semibold">
//               <td colSpan={3} className="p-2 text-right">
//                 Grand Total
//               </td>

//               {dates.map((d) => (
//                 <td key={d} className="p-2">
//                   {Math.floor(Math.random() * 10)}
//                 </td>
//               ))}
//             </tr>
//           </tfoot>
//         </table>
//       </div>
//     </div>
//   );
// }
